import React from 'react';
import { Link } from 'react-router-dom';
import { WEALTH_SERVICES } from '../data/siteData';
import Icon from '../components/Icon';
import { useScrollReveal } from '../hooks/useScrollReveal';
import './WealthManagement.css';
import home from '../assets/welthm.jpeg';
import wealthImage from '../assets/npss.png';

function WealthCard({ s, index }) {
  const { ref, visible } = useScrollReveal();
  // Encode the service name for URL
  const serviceName = encodeURIComponent(s.short || s.name);
  
  return (
    <div ref={ref} id={s.id} className={`wm-card${visible ? ' visible' : ''}`} style={{ transitionDelay: `${(index % 3) * 0.1}s` }}>
      <div className="wmc-top">
        <Icon icon={s.icon} className="wmc-icon" size={40} />
        <div className="wmc-header">
          <div className="wmc-name">{s.name}</div>
          {s.minInvestment && (
            <div className="wmc-min">Min Investment: <strong>{s.minInvestment}</strong></div>
          )}
        </div>
      </div>
      <p className="wmc-desc">{s.desc}</p>
      <div className="wmc-risk">
        <span>Risk Level</span>
        <div className="risk-bar">
          <div className={`risk-fill risk-${s.riskLevel?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z-]/g, '')}`} />
        </div>
        <span className="risk-label">{s.riskLevel}</span>
      </div>
      <div className="wmc-features">
        {s.features.map((f, i) => (
          <div key={i} className="wmc-feature"><span>✦</span>{f}</div>
        ))}
      </div>
      <div className="wmc-footer">
        {/* FIXED: Pass service name to contact page */}
        <Link 
          to={`/contact?service=${serviceName}`} 
          className="btn-primary" 
          style={{ fontSize: 12, padding: '11px 22px' }}
        >
          Start Now →
        </Link>
      </div>
    </div>
  );
}

export default function WealthManagement() {
  return (
    <div className="wm-page page-wrapper">
      {/* Hero */}
      <div className="wm-hero">
        <div className="wm-hero-grid" />
        <div className="wm-hero-glow" />
        <div className="wm-hero-content">
          <div className="wm-hero-left">
            <div className="section-label">Wealth Management</div>
            <h1 className="section-title">
              Grow &amp; Protect<br /><span className="green">Your Wealth</span><br />Intelligently
            </h1>
            <p className="section-sub" style={{ marginTop: 20 }}>
              PMS, Smallcase, AIF, Mutual Funds, Stock Market, ETF, IPO, Fixed Deposit, Debenture, Currency, Commodity, Bond & NPS — all expertly managed by G.M. Narendra.
            </p>
            <div style={{ display: 'flex', gap: 14, marginTop: 36, flexWrap: 'wrap' }}>
              {/* FIXED: Pass a generic wealth management service name */}
              <Link to="/contact?service=Wealth%20Management" className="btn-primary">Get Wealth Plan →</Link>
              <a href="tel:9849131273" className="btn-ghost">📞 9849131273</a>
            </div>
          </div>
          <div className="wm-hero-right">
            <img 
              src={home} 
              alt="Wealth Management"
              className="wm-hero-image"
            />
          </div>
        </div>
      </div>

      {/* Philosophy */}
      <section className="wm-philosophy">
        <div className="wmp-inner">
          <div className="wmp-left">
            <div className="section-label">Our Approach</div>
            <h2 className="section-title">A Disciplined<br /><span className="green">Wealth Philosophy</span></h2>
          </div>
          <div className="wmp-right">
            {[
              { icon: '📐', title: 'Asset Allocation First', text: 'The right mix of asset classes is more important than any single stock pick. We start with allocation strategy, then optimize within each asset class.' },
              { icon: '⏳', title: 'Long-Term Perspective', text: 'Wealth is built over years, not days. Our strategies are designed to compound steadily while managing downside risk through all market cycles.' },
              { icon: '🔄', title: 'Dynamic Rebalancing', text: 'Markets evolve and so should your portfolio. We continuously monitor and rebalance to keep your allocation aligned with your goals.' },
              { icon: '💡', title: 'Tax Efficiency', text: 'Every rupee saved in tax is a rupee that compounds. We structure investments to minimize tax outgo through ELSS, NPS, indexation, and strategic harvesting.' },
            ].map((p, i) => {
              const { ref, visible } = useScrollReveal();
              return (
                <div key={i} ref={ref} className={`wmp-point${visible ? ' visible' : ''}`} style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="wmp-icon"><Icon icon={p.icon} size={40} /></div>
                  <div>
                    <div className="wmp-title">{p.title}</div>
                    <div className="wmp-text">{p.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="wm-services">
        <div style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 60px' }}>
          <div className="section-label" id='green1' style={{ justifyContent: 'center' }}>All Wealth Instruments</div>
          <h2 className="section-title"><span className="green" id='green1'>Choose Your Investment Path</span></h2>
          <p className="section-sub" id='green1' style={{ marginTop: 16 }}>Each instrument serves a unique purpose in your wealth journey. We help you choose and combine them optimally.</p>
        </div>
        <div className="wm-grid">
          {WEALTH_SERVICES.map((s, i) => {
            if (i === 9) {
              return (
                <React.Fragment key="image-wrapper">
                  <WealthCard key={s.id} s={s} index={i} />
                  <div className="wm-card-image-container">
                    <img src={wealthImage} alt="Wealth Management" className="wm-card-img" />
                  </div>
                </React.Fragment>
              );
            }
            return <WealthCard key={s.id} s={s} index={i} />;
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="wm-cta">
        <div className="wm-cta-inner">
          <h2 className="section-title">Ready to Start Your<br /><span className="green">Wealth Journey?</span></h2>
          <p className="section-sub" style={{ marginTop: 20, marginBottom: 40 }}>Book a free consultation. No obligations. Just clarity on your financial path forward.</p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* FIXED: Pass wealth management service name */}
            <Link to="/contact?service=Wealth%20Management" className="btn-primary">Book Free Consultation →</Link>
            <a href="tel:9849131273" className="btn-ghost">📞 Call Now</a>
          </div>
        </div>
      </section>
    </div>
  );
}